//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { ReactChild } from 'react';
import styled, { css } from 'styled-components';

import Button, { InvisibleButton } from '@components/atoms/button/Button';
import {
  IButtonStateStyles,
  PrimaryButtonStyles,
  TertiaryButtonStyles,
} from '@components/atoms/button/buttonStyles';
import Icon from '@components/atoms/icon/Icon';
import RequiredPermissionsButton from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { SubtitleText } from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Dialog } from '@components/primitives/dialog/Dialog';
import { isDefined } from '@utils/filterPredicates';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

type ModalWidth = 'medium' | 'wide' | 'extra-wide' | 'full';

export interface IModalPermissionButtonInfo extends IModalButtonInfo {
  permissionName?: string;
}

interface IModalButtonInfo
  extends ITrackableComponent,
    Omit<React.ComponentPropsWithoutRef<'button'>, 'onClick'> {
  buttonContent?: string | React.ReactNode;
  buttonStyles?: IButtonStateStyles;
  isDisabled?: boolean;
  isTooltipDisabled?: boolean;
  onClick?: () => void;
  tooltipContents?: ReactChild;
}

interface BaseProps extends ITrackableComponent {
  className?: string;
  closeModal: (() => void) | undefined;
  contentOnKeyDown?: React.ComponentPropsWithoutRef<
    typeof Content
  >['onKeyDown'];
  contentOverflow?: 'auto' | 'unset';
  contentRef?: React.ComponentProps<typeof Content>['ref'];
  contents: React.ReactChild;
  isModalOpen: boolean;
  shouldCloseOnClickOutside?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldHideOverlay?: boolean;
  title: React.ReactChild;
  width?: ModalWidth;
  zIndexOverlayOverride?: string;
}

type ModalProps = BaseProps &
  (
    | {
        isFooterEnabled?: true;
        primaryButtonInfo: IModalPermissionButtonInfo;
        secondaryButtonInfo?: IModalButtonInfo;
        shouldIncludeSecondaryButton?: boolean;
        shouldIncludeTertiaryButton?: boolean;
        tertiaryButtonInfo?: IModalPermissionButtonInfo;
      }
    | {
        isFooterEnabled: false;
        primaryButtonInfo?: never;
        secondaryButtonInfo?: never;
        shouldIncludeSecondaryButton?: never;
        shouldIncludeTertiaryButton?: never;
        tertiaryButtonInfo?: never;
      }
  );

/**
 * The most common modal styling for easy reuse. Displays a modal with a title, a padded content block,
 * a close button, and the given primary and secondary actions in the footer. Primarily
 * used for confirmation modals for actions like deleting scheduled posts, removing metadata, et cetera.
 */
export const Modal = ({
  className,
  closeModal,
  contentOnKeyDown,
  contentOverflow = 'auto',
  contentRef,
  contents,
  isFooterEnabled = true,
  isModalOpen,
  primaryButtonInfo,
  secondaryButtonInfo = {},
  shouldCloseOnClickOutside = false,
  shouldCloseOnEsc = false,
  shouldHideOverlay = false,
  shouldIncludeSecondaryButton = true,
  shouldIncludeTertiaryButton = false,
  tertiaryButtonInfo = {},
  title,
  trackingId,
  width = 'medium',
  zIndexOverlayOverride,
}: ModalProps) => (
  <Dialog onOpenChange={(value) => !value && closeModal?.()} open={isModalOpen}>
    <Content
      ref={contentRef}
      className={className}
      onEscapeKeyDown={(event) => {
        // Prevent default behavior of closing modal
        // on ESC key down event
        event.preventDefault();
        shouldCloseOnEsc && closeModal?.();
      }}
      onKeyDown={contentOnKeyDown}
      onPointerDownOutside={(event) => {
        // Prevent default behavior of closing modal
        // on clicking outside of the modal
        event.preventDefault();
        shouldCloseOnClickOutside && closeModal?.();
      }}
      shouldHideOverlay={shouldHideOverlay}
      trackingId={trackingId}
      width={width}
      zIndexOverlayOverride={zIndexOverlayOverride}
    >
      <ModalHeader>
        <SubtitleText>{title}</SubtitleText>
        {isDefined(closeModal) && (
          <InvisibleButton onClick={closeModal}>
            <Icon name='close' />
          </InvisibleButton>
        )}
      </ModalHeader>
      <ModalBody contentOverflow={contentOverflow} width={width}>
        {contents}
      </ModalBody>
      {isFooterEnabled && isDefined(primaryButtonInfo) && (
        <ModalFooter>
          {shouldIncludeTertiaryButton && (
            <OptionalRequiredPermissionButton
              {...tertiaryButtonInfo}
              buttonStyles={
                tertiaryButtonInfo.buttonStyles ?? TertiaryButtonStyles
              }
              isDisabled={tertiaryButtonInfo.isDisabled}
              onClick={tertiaryButtonInfo.onClick ?? closeModal}
              permissionName={tertiaryButtonInfo.permissionName}
              trackingId={tertiaryButtonInfo.trackingId}
            >
              {tertiaryButtonInfo.buttonContent ?? 'Cancel'}
            </OptionalRequiredPermissionButton>
          )}
          <MainActionsWrapper>
            {shouldIncludeSecondaryButton && (
              <Button
                {...secondaryButtonInfo}
                buttonStyles={
                  secondaryButtonInfo.buttonStyles ?? TertiaryButtonStyles
                }
                isDisabled={secondaryButtonInfo.isDisabled}
                onClick={secondaryButtonInfo.onClick ?? closeModal}
                trackingId={secondaryButtonInfo.trackingId}
              >
                {secondaryButtonInfo.buttonContent ?? 'Cancel'}
              </Button>
            )}
            <OptionalRequiredPermissionButton
              {...primaryButtonInfo}
              buttonStyles={
                primaryButtonInfo.buttonStyles ?? PrimaryButtonStyles
              }
              isDisabled={primaryButtonInfo.isDisabled}
              isTooltipDisabled={primaryButtonInfo.isTooltipDisabled}
              onClick={primaryButtonInfo.onClick ?? closeModal}
              permissionName={primaryButtonInfo.permissionName}
              tooltipContents={primaryButtonInfo.tooltipContents}
              trackingId={primaryButtonInfo.trackingId}
            >
              {primaryButtonInfo.buttonContent ?? 'Continue'}
            </OptionalRequiredPermissionButton>
          </MainActionsWrapper>
        </ModalFooter>
      )}
    </Content>
  </Dialog>
);

interface OptionalRequiredPermissionButtonProps
  extends Omit<
    React.ComponentProps<typeof RequiredPermissionsButton>,
    'permissionName'
  > {
  permissionName?: string;
}

const OptionalRequiredPermissionButton = ({
  buttonStyles = PrimaryButtonStyles,
  isTooltipDisabled,
  permissionName,
  tooltipContents,
  ...buttonProps
}: OptionalRequiredPermissionButtonProps) => {
  return isDefined(permissionName) ? (
    <RequiredPermissionsButton
      buttonStyles={buttonStyles}
      permissionName={permissionName}
      {...buttonProps}
    />
  ) : (
    <SmallTooltip
      appendTarget='body'
      content={tooltipContents}
      interactive={false}
      isDisabled={isTooltipDisabled || !isDefined(tooltipContents)}
      target={<Button buttonStyles={buttonStyles} {...buttonProps} />}
    />
  );
};

const Content = withTracking(
  styled(Dialog.Content)<{ width: ModalWidth }>(
    ({ theme, width, zIndexOverlayOverride }) => css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background-color: ${theme.colors.white};
      ${width === 'full' &&
      `
        height: 100%;
        width: 100%;

        && {
          max-height: 100%;
          max-width: 100%;
        }
      `}
      ${zIndexOverlayOverride &&
      `z-index: ${zIndexOverlayOverride};`} // To ensure the sku modal is displayed above the UIA overlay
    `,
  ),
);

const ModalHeader = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    padding: ${theme.spacings[4]};
  `,
);

const ModalBody = styled.div<{
  width: ModalWidth;
  contentOverflow: 'auto' | 'unset';
}>(
  ({ contentOverflow, theme, width }) => `
    height: 100%;
    max-height: calc(100vh - ${theme.spacings[7]} - ${theme.spacings[7]} - ${theme.spacings[5]} - ${theme.spacings[1]});
    max-width: ${getModalWidth(width)};
    min-width: ${getModalWidth(width)};
    margin: 0 ${theme.spacings[4]};
    padding-bottom: ${theme.spacings[5]};
    overflow-y: ${contentOverflow};
  `,
);

const ModalFooter = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    padding: ${theme.spacings[4]};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  `,
);

const MainActionsWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  gap: ${theme.spacings[2]};
  margin-left: auto;
`,
);

function getModalWidth(width: ModalWidth) {
  switch (width) {
    case 'full':
      return '100%';
    case 'medium':
      return theme.mediumMeasure;
    case 'extra-wide':
      return theme.extraWideMeasure;
    default:
      return theme.wideMeasure;
  }
}
